import { useState } from 'react'
import AuthLayout from '@/layouts/AuthLayout'
import type { PricingProps } from '@/types'

type Currency = 'GBP' | 'USD' | 'EUR'
type Interval = 'monthly' | 'annual'

export default function Pricing({ currency: detected, prices, pro_features, enterprise_features }: PricingProps) {
  const [currency, setCurrency] = useState<Currency>(detected)
  const [interval, setInterval] = useState<Interval>('monthly')

  const price = prices[currency]
  const amount = interval === 'monthly' ? price.monthly : price.annual

  return (
    <AuthLayout title="Pricing" maxWidth="max-w-3xl">
      <div className="card bg-base-100 shadow-lg w-full">
        <div className="card-body">
          <h1 className="text-3xl font-bold text-center">Simple, flat pricing</h1>
          <p className="text-center text-base-content/60 mt-2">
            One plan, all features. 30-day free trial, no credit card required.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 mt-6">
            <div className="join">
              {(['GBP', 'USD', 'EUR'] as Currency[]).map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  className={`btn btn-sm join-item ${currency === c ? 'btn-primary' : ''}`}
                >
                  {prices[c].symbol} {c}
                </button>
              ))}
            </div>

            <div className="join">
              {(['monthly', 'annual'] as Interval[]).map((i) => (
                <button
                  key={i}
                  onClick={() => setInterval(i)}
                  className={`btn btn-sm join-item ${interval === i ? 'btn-primary' : ''}`}
                >
                  {i === 'monthly' ? 'Monthly' : 'Annual (save ~15%)'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <div className="card border-2 border-primary">
              <div className="card-body">
                <span className="badge badge-primary badge-sm self-start">Most popular</span>
                <h2 className="card-title text-2xl mt-2">Pro</h2>
                <div className="mt-2">
                  <span className="text-4xl font-bold">{price.symbol}{amount}</span>
                  <span className="text-base-content/60 ms-1">/ {interval === 'monthly' ? 'month' : 'year'}</span>
                </div>
                <p className="text-xs text-base-content/40">All prices excl. VAT/sales tax</p>

                <ul className="space-y-2 text-sm mt-4">
                  {pro_features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <span className="iconify lucide--check size-4 text-primary mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <a href="/signup" className="btn btn-primary btn-block mt-6">
                  Start 30-day free trial
                </a>
                <p className="text-center text-xs text-base-content/40 mt-2">No credit card required</p>
              </div>
            </div>

            <div className="card border border-base-300">
              <div className="card-body">
                <span className="badge badge-soft badge-sm self-start">For larger organizations</span>
                <h2 className="card-title text-2xl mt-2">Enterprise</h2>
                <div className="mt-2">
                  <span className="text-3xl font-bold">Let's talk</span>
                </div>
                <p className="text-xs text-base-content/40">Custom pricing, billed annually</p>

                <ul className="space-y-2 text-sm mt-4">
                  {enterprise_features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <span className="iconify lucide--check size-4 text-base-content/60 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="mailto:hello@envisialearning.com?subject=ImpactCheck%20Enterprise%20enquiry"
                  className="btn btn-outline btn-block mt-6"
                >
                  Contact us
                </a>
                <p className="text-center text-xs text-base-content/40 mt-2">We'll respond within 1 business day</p>
              </div>
            </div>
          </div>

          <p className="text-center text-sm mt-6">
            Already have an account? <a href="/login" className="link link-primary">Log in</a>
          </p>
        </div>
      </div>
    </AuthLayout>
  )
}
